import Box from '@mui/material/Box'
import Snackbar, { type SnackbarProps } from '@mui/material/Snackbar'
import type { SxProps, Theme } from '@mui/material/styles'
import { supportChatInset } from '@tm/ui/atomic/SupportChat/supportChatInset'
import { createContext, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Toasts that stay up until dismissed (a reminder, an ad) sit apart from the action toasts at the
 * bottom centre, which time out after a few seconds and would otherwise render underneath them.
 * On an org at its contact limit the import result is the only message saying rows were left out.
 *
 * Wider screens: the bottom-right corner, just above the support launcher. Phones have no free
 * corner, so the toasts span the width as usual and are lifted clear of a one-line action toast.
 */
const cornerSx = {
  position: 'fixed',
  // The launcher's inset is square, so it also clears the launcher vertically. Views without a
  // launcher leave the variable unset; the 84px floor still clears a one-line action toast there,
  // which on a 600–900px screen would otherwise run under a right-aligned 560px banner.
  bottom: { xs: 104, sm: `max(84px, ${supportChatInset})` },
  // The phone inset matches the theme's Snackbar override, which ends toasts short of the launcher.
  right: { xs: `calc(8px + ${supportChatInset})`, sm: 24 },
  left: { xs: 8, sm: 'auto' },
} satisfies SxProps<Theme>

// undefined: no stack around the toast. null: a stack whose box has not mounted yet, for the one
// render before its ref fires.
const PersistentToastSlot = createContext<HTMLElement | null | undefined>(undefined)

/**
 * The corner the persistent toasts share. Each one is a separate fixed Snackbar otherwise, and
 * two in one corner would cover each other; inside the stack they sit one above the other.
 * Mount it once around every view that can show more than one. Without it a persistent toast
 * still leaves the bottom centre, it only cannot stack.
 */
export function PersistentToastStack({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  return (
    <PersistentToastSlot.Provider value={slot}>
      {children}
      <Box
        ref={setSlot}
        sx={[
          cornerSx,
          {
            zIndex: 'snackbar',
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'stretch', sm: 'flex-end' },
            gap: 1,
            // The box spans the corner even where no toast is; clicks there belong to the page.
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' },
          },
        ]}
      />
    </PersistentToastSlot.Provider>
  )
}

/** A Snackbar for a toast that stays up until dismissed. Takes Snackbar's props minus placement. */
export function PersistentSnackbar({ sx, ...props }: Omit<SnackbarProps, 'anchorOrigin'>) {
  const slot = useContext(PersistentToastSlot)
  const extraSx = Array.isArray(sx) ? sx : [sx]

  // Rendering a fixed Snackbar here would mount it twice: once in place, then again in the portal.
  if (slot === null) return null

  if (slot === undefined) {
    return (
      <Snackbar {...props} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} sx={[cornerSx, ...extraSx]} />
    )
  }

  return createPortal(
    <Snackbar
      {...props}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      // The stack positions its toasts; a fixed Snackbar would pull itself out of the column.
      sx={[{ position: 'static' }, ...extraSx]}
    />,
    slot
  )
}
